/**
 * @module FCElement
 * @description Base custom-element class for the FitCheck theme. Provides automatic `data-ref`
 * tracking via MutationObserver and declarative event-listener wiring for all theme components.
 * @requires @fc/core
 * @requires @fc/utilities
 */
import { ShadowBase } from '@fc/core';
import { requestIdleCallback } from '@fc/utilities';

/**
 * @typedef {Record<string, Element | Element[] | undefined>} Refs
 */

/**
 * @template {Refs} T
 * @typedef {T & Refs} RefsType
 */

/**
 * Base class powering custom web components.
 *
 * Tracks descendants tagged with `data-ref` attributes and keeps `refs` in sync via a MutationObserver.
 * Also wires up declarative event listeners.
 *
 * @template {Refs} [T=Refs]
 * @extends {ShadowBase}
 */
export class FCElement extends ShadowBase {
  /**
   * Holds references to children marked with `data-ref`.
   *
   * @type {RefsType<T>}
   */
  refs = /** @type {RefsType<T>} */ ({});

  /**
   * Refs that must exist; missing ones will throw.
   *
   * @type {string[] | undefined}
   */
  requiredRefs;

  /**
   * Shared AbortSignal for listeners and async work that should stop when the
   * component disconnects.
   *
   * @type {AbortController | null}
   */
  #lifecycleController = null;

  /**
   * Return the component’s root(s): shadow root when present, otherwise the element itself.
   *
   * @returns {(ShadowRoot | FCElement<T>)[]} The root nodes.
   */
  get roots() {
    return this.shadowRoot ? [this, this.shadowRoot] : [this];
  }

  /**
   * AbortSignal that is cancelled automatically on disconnect.
   *
   * @returns {AbortSignal}
   */
  get lifecycleSignal() {
    if (!this.#lifecycleController) {
      this.#lifecycleController = new AbortController();
    }

    return this.#lifecycleController.signal;
  }

  /**
   * Register an event listener that is automatically cleaned up when the
   * component disconnects.
   *
   * @param {EventTarget | null | undefined} target
   * @param {string} eventType
   * @param {EventListenerOrEventListenerObject} callback
   * @param {AddEventListenerOptions | boolean} [options]
   */
  addManagedEventListener(target, eventType, callback, options = {}) {
    if (!target?.addEventListener || typeof eventType !== 'string' || !callback) return;

    if (typeof options === 'boolean') {
      target.addEventListener(eventType, callback, { capture: options, signal: this.lifecycleSignal });
      return;
    }

    target.addEventListener(eventType, callback, {
      ...options,
      signal: options.signal ?? this.lifecycleSignal,
    });
  }

  /**
   * When inserted into the DOM, initialize listeners and ref tracking.
   */
  connectedCallback() {
    super.connectedCallback();
    registerEventListeners();

    this.#updateRefs();

    requestIdleCallback(() => {
      for (const root of this.roots) {
        this.#mutationObserver.observe(root, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['data-ref'],
          attributeOldValue: true,
        });
      }
    });
  }

  /**
   * Called after a Section Rendering API re-morph; re-sync refs.
   */
  onMorphSync() {
    this.#mutationObserver.takeRecords();
    this.#updateRefs();
  }

  /**
   * When removed from the DOM, stop observing.
   */
  disconnectedCallback() {
    this.#mutationObserver.disconnect();
    this.#lifecycleController?.abort();
    this.#lifecycleController = null;
  }

  /**
   * Build the `refs` map from descendants bearing `data-ref` attributes.
   */
  #updateRefs() {
    const refs = /** @type any */ ({});
    const elements = this.roots.reduce((acc, root) => {
      for (const element of root.querySelectorAll('[data-ref]')) {
        if (!this.#isDescendant(element)) continue;
        acc.add(element);
      }

      return acc;
    }, /** @type {Set<Element>} */ (new Set()));

    for (const ref of elements) {
      const refName = ref.getAttribute('data-ref') ?? '';
      const isArray = refName.endsWith('[]');
      const path = isArray ? refName.slice(0, -2) : refName;

      if (isArray) {
        const array = Array.isArray(refs[path]) ? refs[path] : [];

        array.push(ref);
        refs[path] = array;
      } else {
        refs[path] = ref;
      }
    }

    if (this.requiredRefs?.length) {
      for (const ref of this.requiredRefs) {
        if (!(ref in refs)) {
          throw new MissingRefError(ref, this);
        }
      }
    }

    this.refs = /** @type {RefsType<T>} */ (refs);
  }

  /**
   * MutationObserver watching subtree changes that could affect refs.
   *
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    if (
      mutations.some(
        (m) =>
          (m.type === 'attributes' && this.#isDescendant(m.target)) ||
          (m.type === 'childList' && [...m.addedNodes, ...m.removedNodes].some(this.#isDescendant)),
      )
    ) {
      this.#updateRefs();
    }
  });

  /**
   * Is a given node part of this component’s subtree (including shadow boundaries)?
   *
   * @param {Node} node - Node to test.
   * @returns {boolean} True if the node belongs to this component.
   */
  #isDescendant = (node) => getClosestComponent(getAncestor(node)) === this;
}

/**
 * Get a node’s parent or the host when inside a shadow root.
 *
 * @param {Node} node
 * @returns {Node | null}
 */
function getAncestor(node) {
  if (node.parentNode) return node.parentNode;

  const root = node.getRootNode();
  if (root instanceof ShadowRoot) return root.host;

  return null;
}

/**
 * Climb up the tree to find the nearest `FCElement` instance.
 *
 * @param {Node | null} node
 * @returns {HTMLElement | null}
 */
function getClosestComponent(node) {
  if (!node) return null;
  if (node instanceof FCElement) return node;
  if (node instanceof HTMLElement && node.tagName.toLowerCase().endsWith('-component')) return node;

  const ancestor = getAncestor(node);
  if (ancestor) return getClosestComponent(ancestor);

  return null;
}

/**
 * One-time setup for global delegated event listeners used by components.
 */
const DELEGATED_EVENT_TYPES = [
  'click',
  'change',
  'select',
  'focus',
  'blur',
  'submit',
  'input',
  'keydown',
  'keyup',
  'toggle',
];
const NON_BUBBLING_DELEGATED_TYPES = new Set(['focus', 'blur']);
const EXPENSIVE_DELEGATED_TYPES = new Set(['pointerenter', 'pointerleave']);

let initialized = false;

function registerEventListeners() {
  if (initialized) return;
  initialized = true;

  for (const eventType of [...DELEGATED_EVENT_TYPES, ...EXPENSIVE_DELEGATED_TYPES]) {
    const bindingAttribute = `@${eventType}`;
    document.addEventListener(eventType, (event) => handleDelegatedEvent(event, bindingAttribute), { capture: true });
  }
}

/**
 * Handle one delegated DOM event for component bindings.
 *
 * @param {Event} event
 * @param {string} bindingAttribute
 */
function handleDelegatedEvent(event, bindingAttribute) {
  const boundElement = getBoundElement(event);
  if (!boundElement) return;

  const { selector, methodName, dataToken } = parseBinding(boundElement, bindingAttribute);
  if (!methodName) return;

  const instance = resolveBindingInstance(boundElement, selector);
  if (!(instance instanceof FCElement)) return;

  const callback = /** @type {any} */ (instance)[methodName];
  if (typeof callback !== 'function') return;

  const proxiedEvent = createDelegatedEventProxy(event, boundElement);

  try {
    /** @type {(Event | Data)[]} */
    const args = [proxiedEvent];
    if (dataToken) args.unshift(parseData(dataToken));
    callback.call(instance, ...args);
  } catch (error) {
    console.error(error); // eslint-disable-line no-console
  }
}

/**
 * Resolve the element that contains the delegated event binding.
 *
 * @param {Event} event
 * @returns {Element | null}
 */
function getBoundElement(event) {
  const target = event.composedPath?.()[0] ?? event.target;
  if (!(target instanceof Element)) return null;

  if (target.hasAttribute(`@${event.type}`)) return target;
  if (EXPENSIVE_DELEGATED_TYPES.has(event.type)) return null;

  return event.bubbles || NON_BUBBLING_DELEGATED_TYPES.has(event.type) ? target.closest(`[\\@${event.type}]`) : null;
}

/**
 * Parse a declarative handler binding from an element.
 *
 * @param {Element} element
 * @param {string} attribute
 * @returns {{selector: string, methodName: string, dataToken: string | null}}
 */
function parseBinding(element, attribute) {
  const raw = element.getAttribute(attribute) ?? '';
  const [selector = '', methodToken = ''] = raw.split('/');
  const methodName = methodToken.replace(/\?.*/, '');

  // Extract the last segment after `?` or `/` (no lookbehind for Safari 16.0)
  const matches = raw.match(/([\/\?][^\/\?]+)([\/\?][^\/\?]+)$/);
  const dataToken = matches ? matches[2] : null;

  return { selector, methodName, dataToken };
}

/**
 * Resolve the component instance targeted by a declarative binding.
 *
 * @param {Element} element
 * @param {string} selector
 * @returns {Element | null}
 */
function resolveBindingInstance(element, selector) {
  if (!selector) return getClosestComponent(element);
  return selector.startsWith('#') ? document.querySelector(selector) : element.closest(selector);
}

/**
 * Build an event proxy so `event.target` maps to the declarative handler element.
 *
 * @param {Event} event
 * @param {Element} element
 * @returns {Event}
 */
function createDelegatedEventProxy(event, element) {
  if (event.target === element) return event;

  return new Proxy(event, {
    get(target, property) {
      if (property === 'target') return element;

      const value = Reflect.get(target, property);
      return typeof value === 'function' ? value.bind(target) : value;
    },
  });
}

/**
 * Parse a data fragment from the attribute payload.
 *
 * @param {string} str
 * @returns {Object|Array<string|number>|string}
 */
function parseData(str) {
  const delimiter = str[0];
  const data = str.slice(1);

  return delimiter === '?'
    ? Object.fromEntries(
        Array.from(new URLSearchParams(data).entries()).map(([key, value]) => [key, parseValue(value)]),
      )
    : parseValue(data);
}

/**
 * @typedef {Object|Array<string|number>|string} Data
 */

/**
 * Coerce a string to boolean/number when appropriate, fallback to string.
 *
 * @param {string} str
 * @returns {Data}
 */
function parseValue(str) {
  if (str === 'true') return true;
  if (str === 'false') return false;

  const maybeNumber = Number(str);
  if (!isNaN(maybeNumber) && str.trim() !== '') return maybeNumber;

  return str;
}

/**
 * Error thrown when a required `data-ref` is missing.
 */
class MissingRefError extends Error {
  /**
   * @param {string} ref
   * @param {FCElement} component
   */
  constructor(ref, component) {
    super(`Required ref "${ref}" not found in component ${component.tagName.toLowerCase()}`);
  }
}
